'use client'

import * as React from 'react'
import { Lock, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSubscription } from '@/lib/subscription'
import { UpgradePrompt } from './upgrade-prompt'
import type { PremiumFeature } from '@/lib/types'

interface PremiumGateProps {
  /** Which premium feature this gate protects. */
  feature: PremiumFeature
  /** Content to render when the user has access. */
  children: React.ReactNode
  /**
   * Fallback content for free users. If not provided, the children
   * are rendered but clicking triggers the upgrade prompt.
   */
  fallback?: React.ReactNode
}

/**
 * Wraps premium content. If the user is on the free plan, shows the
 * upgrade prompt when they interact instead of rendering the children.
 *
 * Usage:
 *   <PremiumGate feature="just_the_facts">
 *     <AiSummaryButton ... />
 *   </PremiumGate>
 */
export function PremiumGate({ feature, children, fallback }: PremiumGateProps) {
  const { canUse } = useSubscription()
  const [showPrompt, setShowPrompt] = React.useState(false)

  if (canUse(feature)) {
    return <>{children}</>
  }

  if (fallback) {
    return (
      <>
        {fallback}
        <UpgradePrompt open={showPrompt} onOpenChange={setShowPrompt} feature={feature} />
      </>
    )
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowPrompt(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setShowPrompt(true)
        }}
        className="cursor-pointer"
      >
        {children}
      </div>
      <UpgradePrompt open={showPrompt} onOpenChange={setShowPrompt} feature={feature} />
    </>
  )
}

/**
 * Small inline badge that indicates a Penthouse-only feature.
 * Clicking opens the upgrade prompt.
 */
export function PenthouseBadge({
  feature,
  className,
}: {
  feature: PremiumFeature
  className?: string
}) {
  const { canUse } = useSubscription()
  const [showPrompt, setShowPrompt] = React.useState(false)

  if (canUse(feature)) return null

  return (
    <>
      <button
        onClick={() => setShowPrompt(true)}
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium',
          'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20',
          'hover:bg-amber-500/20 transition-colors cursor-pointer',
          className
        )}
        title="Penthouse feature — click to learn more"
      >
        <Crown className="h-3 w-3" />
        Penthouse
      </button>
      <UpgradePrompt open={showPrompt} onOpenChange={setShowPrompt} feature={feature} />
    </>
  )
}

/**
 * Lock overlay that can be placed on top of a UI element for free users.
 */
export function PenthouseLock({
  feature,
  className,
}: {
  feature: PremiumFeature
  className?: string
}) {
  const { canUse } = useSubscription()
  const [showPrompt, setShowPrompt] = React.useState(false)

  if (canUse(feature)) return null

  return (
    <>
      <button
        onClick={() => setShowPrompt(true)}
        className={cn(
          'absolute inset-0 z-10 flex items-center justify-center rounded-lg',
          'bg-background/60 backdrop-blur-[1px] cursor-pointer transition-colors',
          'hover:bg-background/40',
          className
        )}
        title="Penthouse feature — click to upgrade"
      >
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
          <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Penthouse</span>
        </div>
      </button>
      <UpgradePrompt open={showPrompt} onOpenChange={setShowPrompt} feature={feature} />
    </>
  )
}
